<template>
  <Teleport to="body">
    <div
      class="cr-modal__overlay"
      data-pendo-ds="modal"
      :data-testid="dataTestid"
      @click.self="clickAway"
    >
      <div
        class="cr-modal cr-br-large"
        :class="{'cr-modal--no-title-gap': !title && !hasHeaderSlot, 'cr-modal--dark-mode': darkMode}"
        to="body"
      >
        <slot name="top-decorator" />
        <div
          class="cr-modal__header"
          :class="{'cr-modal__header--border-bottom': title && showDividerLine}"
        >
          <slot name="header">
            <h2 class="title-s-medium">
              {{ title }}
            </h2>
          </slot>
          <div
            v-if="showCloseButton"
            class="cr-modal__header-close"
            :data-testid="`${dataTestid}-close`"
          >
            <BaseButton
              :data-testid="`${dataTestid}-close-button`"
              :emphasis="darkMode ? 'high' : 'low'"
              icon="fa-solid fa-xmark"
              theme="mono"
              @click="closeModal('header-btn')"
            />
          </div>
        </div>
        <div
          ref="modal-content"
          class="cr-modal__content"
        >
          <slot name="content">
            Default content goes here.
          </slot>
        </div>
        <slot name="buttons" />
      </div>
    </div>
  </Teleport>
</template>

<script>
import BaseButton from './BaseButton.vue'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { faXmark as farXmark } from '@fortawesome/pro-regular-svg-icons'

library.add(faXmark, farXmark)

export default {
  name: 'BaseModal',
  components: {
    BaseButton,
  },
  props: {
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    showDividerLine: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
    dataTestid: {
      type: String,
      required: false,
      default: 'modal',
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    overlayPadding: {
      type: String,
      default: 'var(--space-10)',
    },
  },
  emits: ['close'],
  computed: {
    hasHeaderSlot() {
      return this.$slots['header']
    },
  },
  mounted() {
    document.body.classList.add('body--modal-open')
  },
  beforeUnmount() {
    document.body.classList.remove('body--modal-open')
  },
  methods: {
    closeModal(source) {
      this.$emit('close', { source })
    },
    clickAway() {
      if (this.closeOnOutsideClick) {
        this.closeModal('overlay')
      }
    },
  },
}
</script>

<style lang="scss">

.body--modal-open {
  overflow: hidden;
}

</style>

<style scoped lang="scss">

h2 {
  margin: 0;
}

.cr-modal > :deep(*) {
  width: 100%;
}

.cr-modal__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 100000;
  padding: v-bind(overlayPadding);
  backdrop-filter: brightness(80%);
  background: rgba(0, 0, 0, 0.25);
}

.cr-modal {
  display: none;
  animation: fadein 0.3s;
  background-color: white;
  width: auto;
  min-width: 300px;
  max-width: fit-content;
  padding-top: var(--space-4);
  padding-bottom: var(--space-4);
  padding-left: var(--space-5);
  padding-right: var(--space-5);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  border-radius: var(--space-4);
  overflow: hidden;
  gap: var(--space-4);
  max-height: 100%;
}

.cr-modal--no-title-gap {
  .cr-modal__content {
    margin-top: calc(0px - var(--space-3));
  }
}

.cr-modal--dark-mode {
  background: var(--pepper-1000);
  color: var(--salt);
  .cr-modal__header--border-bottom {
    border-bottom: none;
  }
}

.cr-modal__header--border-bottom {
  border-bottom: 1px solid var(--pepper-200);
}
.cr-modal__header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  p {
    margin-bottom: 0px;
    padding-bottom: var(--space-2);
  }
}

.cr-modal__content {
  overflow-y: auto;
  max-height: 100%;
  width: 100%;
}

.cr-modal__header-close {
  --font-size-2: 16px;
  margin-right: -12px;
}

@media (max-width: 560px) {
  .cr-modal {
    min-width: 100%;
  }
  .cr-modal__overlay {
    padding: var(--space-4) var(--space-2);
  }

}

@keyframes fadein {
	from {
		opacity:0;
	}
	to {
		opacity:1;
	}
}

</style>
